import { Box, Typography } from '@mui/material'
import T from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import ConfirmBar from 'components/atoms/ConfirmBar'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import TitleBar from 'components/atoms/TitleBar'

import CapField from './CapField'

const createColumns = (t, items) => [
  {
    accessor: 'partnerGroup',
    Header: t('sessions.caps.table.partnerGroup'),
  },
  {
    accessor: 'partner',
    Header: t('sessions.caps.table.partner'),
  },
  {
    accessor: 'capFirstChoice',
    Header: (
      <TableHeaderCell
        label={t('sessions.caps.table.firstChoice')}
        errorsNumber={
          items.filter((item) => item.hasCap && !item.capFirstChoice).length
        }
      />
    ),
  },
  {
    accessor: 'blindFirstChoice',
    Header: (
      <TableHeaderCell
        label={t('sessions.blindCaps.table.firstChoice')}
        errorsNumber={
          items.filter((item) => item.hasBlindCap && !item.blindFirstChoice)
            .length
        }
      />
    ),
  },
]

export default function SessionsCaps({
  session,
  updateCap,
  updateBlindCap,
  handleValidation,
  handleConfirm,
  confirmed,
  readOnly,
  contentIsReadOnly = false,
}) {
  const { t } = useTranslation()

  const caps = useMemo(() => session.caps || [], [session.caps])
  const blindCaps = useMemo(() => session.blindCaps || [], [session.blindCaps])

  const combinedItems = useMemo(() => {
    const capsById = caps.reduce((result, item) => {
      result[item.partnerId] = item
      return result
    }, {})
    const blindCapsById = blindCaps.reduce((result, item) => {
      result[item.partnerId] = item
      return result
    }, {})

    const partnerIds = Array.from(
      new Set([
        ...caps.map((item) => item.partnerId),
        ...blindCaps.map((item) => item.partnerId),
      ])
    )

    return partnerIds.map((partnerId) => {
      const cap = capsById[partnerId]
      const blindCap = blindCapsById[partnerId]
      return {
        partnerId,
        partnerGroup: cap?.partnerGroup || blindCap?.partnerGroup,
        partner: cap?.name || blindCap?.name,
        capFirstChoice: cap?.firstChoice,
        blindFirstChoice: blindCap?.firstChoice,
        hasCap: !!cap,
        hasBlindCap: !!blindCap,
      }
    })
  }, [blindCaps, caps])

  const columns = useMemo(
    () => createColumns(t, combinedItems),
    [combinedItems, t]
  )

  const handleCapChange = (event) => {
    const [scope, partnerId, type] = event.target.id.split('.')
    const value = Number(event.target.value)

    if (scope === 'blind') {
      updateBlindCap(session.sessionId, partnerId, type, value)
      return
    }

    updateCap(session.sessionId, partnerId, type, value)
  }

  const preparedItems = combinedItems.map((item) => ({
    partnerGroup: item.partnerGroup,
    partner: item.partner,
    blindFirstChoice: item.hasBlindCap ? (
      <CapField
        id={`blind.${item.partnerId}.first`}
        value={item.blindFirstChoice}
        readOnly={!!session?.confirmationAcceptedAt}
        handleCapChange={handleCapChange}
        canBeEmpty
        disabled={contentIsReadOnly}
      />
    ) : (
      '-'
    ),
    capFirstChoice: item.hasCap ? (
      <CapField
        id={`cap.${item.partnerId}.first`}
        value={item.capFirstChoice}
        readOnly={!!session?.confirmationAcceptedAt}
        handleCapChange={handleCapChange}
        canBeEmpty
        disabled={contentIsReadOnly}
      />
    ) : (
      '-'
    ),
  }))

  const totalMissing = useMemo(
    () =>
      caps.reduce(
        (result, current) => result + Number(!current.firstChoice),
        0
      ) +
      blindCaps.reduce(
        (result, current) => result + Number(!current.firstChoice),
        0
      ),
    [blindCaps, caps]
  )

  return (
    <div data-testid="session-caps">
      <Box mt={1} />
      <ConfirmBar
        label={t(`sessions.caps.confirmButton`)}
        right={
          totalMissing ? (
            <>
              <Typography
                component="span"
                variant="body2"
                color="textSecondary"
                sx={{ paddingRight: (theme) => theme.spacing(0.5) }}
              >
                <strong>{t('sessions.caps.capsMissing')}</strong>
              </Typography>
              <Typography component="span" variant="body2" color="error">
                <strong>{totalMissing}</strong>
              </Typography>
            </>
          ) : null
        }
        disabled={
          !handleValidation() || confirmed || readOnly || contentIsReadOnly
        }
        handleConfirm={handleConfirm}
      />
      <TitleBar
        title={t(`sessions.caps.contentTitle`)}
        description={t(`sessions.caps.contentDescription`)}
      />
      <Table items={preparedItems} columns={columns} id="caps-list" />
    </div>
  )
}

SessionsCaps.propTypes = {
  session: T.object.isRequired,
  updateCap: T.func.isRequired,
  updateBlindCap: T.func.isRequired,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func.isRequired,
  confirmed: T.bool,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
